import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { executeSophiaAction, selectSelectedProducts } from '../features/thread/threadSlice';
import Modal from './Modal';
import { normaliseProductDetails, ProductDetails } from '../utils/product';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import PieChart from './PieChart';
import SimpleImageCarousel from './SimpleImageCarousel';
import { MAPPING_COLUMNS, formatColumnValue } from '../features/products/ListProducts';
import ForecastChart from './ForecastChart';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { ProjectType } from '../api/sophia';

// CSS for details element to rotate arrow on open
const detailsStyles = `
  details[open] summary .arrow {
    transform: rotate(90deg);
  }
  details:not([open]) summary {
    border-bottom: 1px solid black;
    padding-bottom: 0.5rem;
  }
`;

const GalleryStyles = `
  .image-gallery-bullet {
    background: gray !important;
    border: 1px solid gray !important;
  }
  .image-gallery-thumbnail:hover {
    border: inherit !important; /* use whatever the default is */
  }
  .image-gallery-thumbnail.active {
    border: 2px solid #262626 !important;
  }
  .image-gallery-bullet.active {
    background: #262626 !important;
    border: 1px solid #FFFFFF !important;
  }
`;


// Cloudinary image utility functions
function buildCloudinaryImageFromPath(fullPath: string | null | undefined, width: number, height?: number): string | null {
  if (!fullPath) {
    return null;
  }
  const heightParam = height ? `,h_${height}` : '';
  return `https://media.brunellocucinelli.com/image/upload/ar_1:1,c_fill,w_${width}${heightParam},g_north/${fullPath}`;
}

function extractImagePath(imageUrl: string): string | null {
  // Extract full path from existing Cloudinary URL
  // Expected format: https://media.brunellocucinelli.com/image/upload/TRANSFORMATIONS/FULL_PATH
  if (imageUrl.includes('/upload/')) {
    const parts = imageUrl.split('/upload/');
    if (parts.length > 1) {
      const afterUpload = parts[1];
      // Remove existing transformations and keep only the path
      // Look for pattern: transformations/path or just path
      const pathMatch = afterUpload.match(/(?:[^\/]+\/)*(.+)$/);
      if (pathMatch) {
        // Check if there are transformations (contains comma or specific params)
        const segments = afterUpload.split('/');
        // Find where the actual path starts (after transformations)
        let pathStartIndex = 0;
        for (let i = 0; i < segments.length; i++) {
          if (segments[i].includes(',') || segments[i].includes('_') || segments[i].startsWith('ar_') || segments[i].startsWith('c_') || segments[i].startsWith('w_') || segments[i].startsWith('g_')) {
            // This is a transformation parameter, skip it
            pathStartIndex = i + 1;
          } else if (segments[i].startsWith('v') && /^v\d+$/.test(segments[i])) {
            // This is a version number, include it in the path
            break;
          } else {
            break;
          }
        }
        // Reconstruct the path from the identified start
        return segments.slice(pathStartIndex).join('/');
      }
    }
  }
  return null;
}

// Coordinate data utility functions
function parseCoordinateData(product: ProductDetails, similarityData?: Record<string, number>): Array<{ label: string, value: number, color: string }> {
  const coordinateData: Array<{ label: string, value: number, color: string }> = [];
  const colors = ['#71685F', '#33485B', '#9F6460', '#A7674D',
    '#8C7F75', '#688096', '#D4C4C4', '#CE9A85'
  ];

  if (similarityData) {
    Object.entries(similarityData).forEach(([key, value], index) => {
      if (typeof value === 'number' && value > 0) {
        coordinateData.push({
          label: key,
          value: value,
          color: colors[index % colors.length]
        });
      }
    });
  }

  return coordinateData
    .sort((a, b) => b.value - a.value)
}

// Color utility functions
function normalizeRGBToCSS(r: number, g: number, b: number): string {
  // Convert normalized RGB (0-1) to RGB values (0-255)
  const red = Math.round(r * 255);
  const green = Math.round(g * 255);
  const blue = Math.round(b * 255);
  return `rgb(${red}, ${green}, ${blue})`;
}

function rgbToHex(r: number, g: number, b: number): string {
  const red = Math.round(r * 255);
  const green = Math.round(g * 255);  
  const blue = Math.round(b * 255);
  return `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
}

function parseColorComposition(colorData: unknown): Array<{ rgb: [number, number, number], weight: number, color: string, hex: string }> {
  if (!colorData) return [];
  
  try {
    let dataString = '';
    
    if (typeof colorData === 'string') {
      dataString = colorData;
    } else if (typeof colorData === 'object') {
      // If it's already an object, try to convert it to string
      dataString = JSON.stringify(colorData);
    } else {
      return [];
    }
    
    // Custom parser for Python dict format with tuples as keys
    // Format: {(0.23207205568332925, 0.18219571460128112, 0.176117586420475): 9054, ...}
    const results: Array<{ rgb: [number, number, number], weight: number, color: string, hex: string }> = [];
    
    // Remove outer curly braces
    let cleanedString = dataString.trim();
    if (cleanedString.startsWith('{') && cleanedString.endsWith('}')) {
      cleanedString = cleanedString.slice(1, -1);
    }
    
    // Use regex to find pattern: (number, number, number): number
    const tuplePattern = /\(([\d.-]+),\s*([\d.-]+),\s*([\d.-]+)\):\s*([\d.-]+)/g;
    let match;
    
    while ((match = tuplePattern.exec(cleanedString)) !== null) {
      const r = parseFloat(match[1]);
      const g = parseFloat(match[2]);
      const b = parseFloat(match[3]);
      const weight = parseFloat(match[4]);
      
      if (!isNaN(r) && !isNaN(g) && !isNaN(b) && !isNaN(weight)) {
        results.push({
          rgb: [r, g, b],
          weight,
          color: normalizeRGBToCSS(r, g, b),
          hex: rgbToHex(r, g, b)
        });
      }
    }
    
    // Sort by descending weight
    results.sort((a, b) => b.weight - a.weight);
    
    return results;
    
  } catch (error) {
    return [];
  }
}

// Component to display color variants
function ColorCompositionDisplay({ 
  colorComposition, 
  currentSku,
  onVariantClick 
}: { 
  colorComposition: string;
  currentSku?: string;
  onVariantClick?: (sku: string) => void;
}) {
  let colorVariants;
  try {
    colorVariants = JSON.parse(colorComposition);
  } catch (error) {
    console.error('Failed to parse color composition:', error);
    colorVariants = [];
  }
  
  if (!Array.isArray(colorVariants) || colorVariants.length === 0) {
    return <span className="text-gray-500 text-sm">Non ci sono varianti colore</span>;
  }
  
  return (
    <div className="grid grid-cols-3 gap-2">
      {colorVariants.map((variant, index) => {
        // Convert RGB array [r, g, b] with normalized values (0-1) to CSS rgb()
        let backgroundColor = '#CCCCCC'; // Default gray color
        let hasColorData = false;
        
        if (variant.color_composition && Array.isArray(variant.color_composition) && variant.color_composition.length === 3) {
          const [r, g, b] = variant.color_composition;
          if (typeof r === 'number' && typeof g === 'number' && typeof b === 'number') {
            // Convert normalized RGB (0-1) to RGB values (0-255)
            const red = Math.round(r * 255);
            const green = Math.round(g * 255);
            const blue = Math.round(b * 255);
            backgroundColor = `rgb(${red}, ${green}, ${blue})`;
            hasColorData = true;
          }
        }
        
        const isCurrentProduct = variant.sku === currentSku;
        
        const handleClick = () => {
          if (!isCurrentProduct && onVariantClick && variant.sku) {
            onVariantClick(variant.sku);
          }
        };
        
        return (
          <div
            key={variant.sku || index}
            className={`flex items-center gap-3 p-1 -m-1 rounded transition-colors ${
              isCurrentProduct 
                ? 'opacity-75' 
                : 'cursor-pointer hover:bg-gray-50'
            }`}
            onClick={handleClick}
            title={
              isCurrentProduct 
                ? `${variant.cod_colore} - Prodotto corrente` 
                : `${variant.cod_colore}${!hasColorData ? ' - Colore non trovato a sistema' : ''} - Click per vedere dettagli`
            }
          >
            <div
              className="h-8 w-8 flex-shrink-0 relative border border-gray-300"
              style={{
                backgroundColor: backgroundColor
              }}
            >
              {/* Question mark for unknown colors */}
              {!hasColorData && (
                <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-lg font-bold">
                  ?
                </div>
              )}
            </div>
            <span className="text-sm font-medium text-gray-900">
              {variant.cod_colore}{isCurrentProduct ? ' (Questo)' : ''}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Component to display expandable description
function DescriptionField({ description }: { description: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Split description into lines and check if it's longer than 2 lines
  const lines = description.split('\n');
  const shouldTruncate = lines.length > 2 || description.length > 150;
  
  const displayText = shouldTruncate && !isExpanded 
    ? description.slice(0, 150) + (description.length > 150 ? '...' : '')
    : description;
  
  return (
    <div className=" bg-gray-100 px-4 py-3">
      <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">Descrizione SFCC</span>
      <div className="mt-1">
        <p className="text-lg font-semibold text-gray-900 whitespace-pre-wrap">
          {displayText}
        </p>
        {shouldTruncate && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 text-sm font-medium text-black hover:text-gray-700"
          >
            {isExpanded ? 'Mostra meno' : 'Mostra tutto'}
          </button>
        )}
      </div>
    </div>
  );
}

// Component to display expandable text for general use
function ExpandableText({ text }: { text: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Check if text should be truncated (more than 3 lines or 120 characters)
  const lines = text.split('\n');
  const shouldTruncate = lines.length > 3 || text.length > 120;
  
  const displayText = shouldTruncate && !isExpanded 
    ? text.slice(0, 120) + (text.length > 120 ? '...' : '')
    : text;
  
  return (
    <div className="mt-1">
      <p className="text-lg font-semibold text-gray-900 whitespace-pre-wrap">
        {displayText}
      </p>
      {shouldTruncate && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 text-xs font-medium text-black hover:text-gray-700"
        >
          {isExpanded ? 'Mostra meno' : 'Mostra tutto'}
        </button>
      )}
    </div>
  );
}

// Component to display area-based heatmap grid
function HeatmapGrid({ data }: { data: any[] }) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center p-8">
        <p className="text-gray-500">Nessun dato disponibile per la heatmap</p>
      </div>
    );
  }

  // Get color based on classe_quantita
  const getColorForClass = (classe: string): string => {
    const colors: Record<string, string> = {
      'alto-vendente': '#10B981', // Green
      'medio-vendente': '#F59E0B', // Amber/Orange
      'basso-vendente': '#EF4444', // Red
      'zero-vendente': '#9CA3AF', // Gray
      'default': '#9CA3AF' // Gray
    };
    return colors[classe] || colors['default'];
  };

  // Get text color for contrast
  const getTextColorForClass = (classe: string): string => {
    return 'text-white';
  };

  return (
    <div className="w-full p-4">
      <div className="grid grid-cols-3 gap-3">
        {data.map((item, index) => {
          const bgColor = getColorForClass(item.classe_quantita);
          const textColor = getTextColorForClass(item.classe_quantita);
          const isHovered = hoveredIndex === index;
          
          return (
            <div
              key={index}
              className={`relative p-4 shadow-md transition-all duration-200 ${textColor} ${isHovered ? 'scale-105 z-10' : ''}`}
              style={{ backgroundColor: bgColor }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="font-bold text-lg mb-2">{item.area}</div>
              <div className="text-sm opacity-90 mb-1">
                Quantità: {item.quantita_totale?.toLocaleString() || 'N/A'}
              </div>
              <div className="text-xs opacity-75 mt-2 capitalize">
                {item.classe_quantita?.replace('-', ' ') || 'N/A'}
              </div>
              
              {/* Hover tooltip with additional data */}
              {isHovered && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white text-black shadow-xl p-3 z-[100] border-2 border-gray-200">
                  <div className="text-xs space-y-1">
                    <div className="font-semibold text-sm mb-2 border-b pb-1">{item.area}</div>
                    <div><span className="font-medium">SKU:</span> {item.sku || 'N/A'}</div>
                    <div><span className="font-medium">Quantità Totale:</span> {item.quantita_totale?.toLocaleString() || 'N/A'}</div>
                    {item.date_min && <div><span className="font-medium">Data Min:</span> {item.date_min}</div>}
                    {item.date_max && <div><span className="font-medium">Data Max:</span> {item.date_max}</div>}
                    <div className="mt-2 pt-2 border-t">
                      <span className="font-medium">Classe:</span> 
                      <span className="ml-1 capitalize">{item.classe_quantita?.replace('-', ' ') || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="mt-6 flex justify-center gap-6">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10B981' }}></div>
          <span className="text-sm text-gray-700">Alto Vendente</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#F59E0B' }}></div>
          <span className="text-sm text-gray-700">Medio Vendente</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#EF4444' }}></div>
          <span className="text-sm text-gray-700">Basso Vendente</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#9CA3AF' }}></div>
          <span className="text-sm text-gray-700">Zero Vendente</span>
        </div>
      </div>
    </div>
  );
}

// Component to display geographic sales bubble map
function GeographicSalesMap({ data }: { data: any }) {
  // Expected data format: { locations: [{city: string, lat: number, lon: number, sales: number, ...}] }
  const [mapError, setMapError] = useState(false);
  const [hoveredShop, setHoveredShop] = useState<any | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const mapContainerRef = useRef<HTMLDivElement>(null);
  
  if (!data || !data.locations || data.locations.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center p-8">
        <p className="text-gray-500">Nessun dato disponibile per la mappa geografica</p>
      </div>
    );
  }

  // Find max sales for scaling bubble sizes and colors
  const maxSales = Math.max(...data.locations.map((loc: any) => loc.sales));
  const minSales = Math.min(...data.locations.map((loc: any) => loc.sales));
  
  // Scale bubble radius based on sales (sqrt for better visual perception)
  const scaleRadius = (sales: number) => {
    const normalized = (sales - minSales) / (maxSales - minSales || 1);
    return Math.max(4, Math.min(20, 5 + normalized * 15)); // Min 4px, max 20px
  };

  // Color based on sales intensity (gradient from low to high sales)
  const getSalesColor = (sales: number): string => {
    const normalized = (sales - minSales) / (maxSales - minSales || 1);
    
    // Color gradient: Low (Yellow) -> Medium (Orange) -> High (Red)
    if (normalized < 0.33) {
      // Low sales: Yellow to Light Orange
      const intensity = normalized / 0.33;
      const red = 255;
      const green = Math.round(255 - intensity * 75);
      return `rgb(${red}, ${green}, 0)`;
    } else if (normalized < 0.66) {
      // Medium sales: Orange
      const intensity = (normalized - 0.33) / 0.33;
      const red = 255;
      const green = Math.round(180 - intensity * 80);
      return `rgb(${red}, ${green}, 0)`;
    } else {
      // High sales: Dark Orange to Red
      const intensity = (normalized - 0.66) / 0.34;
      const red = 255;
      const green = Math.round(100 - intensity * 100);
      return `rgb(${red}, ${green}, 0)`;
    }
  };

  // Get sales category label
  const getSalesCategory = (sales: number): string => {
    const normalized = (sales - minSales) / (maxSales - minSales || 1);
    if (normalized < 0.33) return 'Basse';
    if (normalized < 0.66) return 'Medie';
    return 'Alte';
  };

  // Natural Earth TopoJSON URL (110m resolution)
  const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

  if (mapError) {
    return (
      <div className="flex gap-4 p-4">
        <div className="w-full h-[500px] flex items-center justify-center border border-gray-200 rounded-lg bg-gray-50">
          <div className="text-center">
            <p className="text-gray-600 mb-2">Errore nel caricamento della mappa</p>
            <button 
              onClick={() => setMapError(false)}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Riprova
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex gap-4 p-4 w-full">
      <div ref={mapContainerRef} className="border border-gray-200 shadow-sm bg-gradient-to-b from-blue-50 to-blue-100 flex-1 relative overflow-visible" style={{ minWidth: 0, height: 'fit-content' }}>
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 140,
            center: [10, 30]
          }}
          width={800}
          height={400}
        >
          <ZoomableGroup>
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#D6D6DA"
                    stroke="#FFFFFF"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', fill: '#C0C0C4' },
                      pressed: { outline: 'none' }
                    }}
                  />
                ))
              }
            </Geographies>

            {/* Shop markers */}
            {data.locations.map((location: any, index: number) => {
              const radius = scaleRadius(location.sales);
              const color = getSalesColor(location.sales);
              
              return (
                <Marker key={index} coordinates={[location.lon, location.lat]}>
                  {/* Outer glow */}
                  <circle
                    r={radius * 1.5}
                    fill={color}
                    opacity={0.3}
                    className="transition-all duration-200"
                  />
                  {/* Invisible larger circle for better hover detection */}
                  <circle
                    r={Math.max(radius * 2, 8)}
                    fill="transparent"
                    className="cursor-pointer"
                    onMouseEnter={(e) => {
                      setHoveredShop(location);
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top });
                    }}
                    onMouseLeave={() => setHoveredShop(null)}
                  />
                  {/* Main circle */}
                  <circle
                    r={radius}
                    fill={color}
                    stroke="#fff"
                    strokeWidth={2}
                    opacity={0.9}
                    className="pointer-events-none transition-all duration-200"
                  />
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>
      </div>
      
      {/* Hover tooltip - rendered as fixed positioned element outside map container */}
      {hoveredShop && (
        <div 
          className="fixed bg-white text-black shadow-2xl p-3 border-2 border-gray-300 pointer-events-none"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y - 10}px`,
            transform: 'translate(-50%, -100%)',
            minWidth: '200px',
            zIndex: 9999
          }}
        >
          <div className="text-xs space-y-1">
            <div className="font-semibold text-sm mb-2 border-b pb-1">
              {hoveredShop.city || hoveredShop.cod_negozio || 'N/A'}
            </div>
            <div><span className="font-medium">Codice Negozio:</span> {hoveredShop.cod_negozio || 'N/A'}</div>
            <div><span className="font-medium">SKU:</span> {hoveredShop.sku || 'N/A'}</div>
            <div><span className="font-medium">Quantità Totale:</span> {hoveredShop.sales?.toLocaleString() || hoveredShop.quantita_totale?.toLocaleString() || 'N/A'}</div>
            {hoveredShop.quantita_media && (
              <div><span className="font-medium">Quantità Media:</span> {hoveredShop.quantita_media.toFixed(3)}</div>
            )}
            {hoveredShop.quantita_mediana && (
              <div><span className="font-medium">Mediana:</span> {hoveredShop.quantita_mediana.toFixed(3)}</div>
            )}
            {hoveredShop.quantita_std && (
              <div><span className="font-medium">Dev. Standard:</span> {hoveredShop.quantita_std.toFixed(3)}</div>
            )}
            {hoveredShop.date_min && <div><span className="font-medium">Data Min:</span> {hoveredShop.date_min}</div>}
            {hoveredShop.date_max && <div><span className="font-medium">Data Max:</span> {hoveredShop.date_max}</div>}
            <div className="mt-2 pt-2 border-t">
              <span className="font-medium">Categoria:</span> 
              <span className="ml-1">{getSalesCategory(hoveredShop.sales || hoveredShop.quantita_totale || 0)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-col gap-3 w-64 bg-white border border-gray-200 p-4 shadow-sm self-start" style={{ height: '300px' }}>
        <div className="text-sm font-bold text-gray-800 uppercase border-b border-gray-200 pb-2">
          Legenda
        </div>
        
        {/* Sales intensity scale */}
        <div className="flex flex-col gap-2">
          <div className="text-sm font-semibold text-gray-700">Intensità Vendite</div>
          <div className="flex flex-col gap-2 px-1">
            {/* Gradient bar */}
            <div className="h-8" style={{
              background: 'linear-gradient(to right, rgb(255, 255, 0), rgb(255, 180, 0), rgb(255, 100, 0), rgb(255, 0, 0))'
            }} />
            <div className="flex justify-between text-xs text-gray-600">
              <span>Basse</span>
              <span>Medie</span>
              <span>Alte</span>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
          <div className="text-sm font-semibold text-gray-700">Statistiche</div>
          <div className="text-xs space-y-1 text-gray-600">
            <div><span className="font-medium">Negozi Totali:</span> {data.locations.length}</div>
            <div><span className="font-medium">Vendite Min:</span> {minSales.toLocaleString()}</div>
            <div><span className="font-medium">Vendite Max:</span> {maxSales.toLocaleString()}</div>
            <div><span className="font-medium">Vendite Totali:</span> {data.locations.reduce((sum: number, loc: any) => sum + (loc.sales || loc.quantita_totale || 0), 0).toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Component to display image carousel
function ImageCarousel({ images, productCode }: { images: string[], productCode: string }) {
  if (!images || images.length === 0) {
    return (
      <div className="w-full h-64 bg-gray-200 rounded-xl flex items-center justify-center">
        <span className="text-gray-500">No images available</span>
      </div>
    );
  }

  // Convert images to react-image-gallery format with different sizes
  const galleryImages = images.map((image, index) => {
    const imagePath = extractImagePath(image);
    
    const originalUrl = imagePath ? buildCloudinaryImageFromPath(imagePath, 800) : null;
    const thumbnailUrl = imagePath ? buildCloudinaryImageFromPath(imagePath, 80) : null;
    
    return {
      original: originalUrl || image, // Fallback to original if generation fails
      thumbnail: thumbnailUrl || image, // Fallback to original if generation fails
      description: productCode
    };
  });

  return (
    <div className="w-full max-w-md mx-auto"> {/* Reduced from max-w-lg to max-w-md */}
      <ImageGallery
        items={galleryImages}
        showThumbnails={false && (images.length > 1)}
        showPlayButton={false}
        showFullscreenButton={false}
        // showNav={images.length > 1}
        autoPlay={false}
        slideDuration={300}
        showNav={false}
        showBullets={images.length > 1}
        slideInterval={3000}
        thumbnailPosition="bottom"
        renderItem={(item) => (
          <div className="image-gallery-item">
            <img
              src={item.original}
              alt={item.description}
              className="image-gallery-image"
              style={{ maxHeight: '300px', objectFit: 'contain', width: '100%' }} // Show full image without cropping
            />
          </div>
        )}
      />
    </div>
  );
}

interface DetailsModalProps {
  sku: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSkuChange?: (newSku: string) => void;
  projectType?: ProjectType;
}

interface ModalStackItem {
  sku: string;
  product: ProductDetails | null;
  similarProducts: ProductDetails[];
  similarityDataMap: Record<string, any>;
  activeTab: 'dati' | 'simili' | 'venduti';
  includeCodModelloSelection: boolean;
  selectedSeasons: string[];
}

interface Prediction {
  date: string;
  quantity: Record<number, number>;
}

interface ForecastResponse {
  shop_id: string;
  product_id: string | null;
  season_id: number;
  forecasts: Prediction[];
}

interface SophiaDetailsResponse {
  'global-page-data'?: {
    sku_data?: Record<string, unknown>;
    similarity_data?: Array<{ 
      sku_data?: Record<string, unknown>;
      similarity?: Record<string, number>;
      // 'override' quando lo SKU arriva dalla tabella delle sovrascritture del buyer.
      source?: 'model' | 'override';
    }>;
    stats_heatmap?: {
      stats?: any[];
    };
    stats_world?: {
      stats?: any[];
    };
    forecast_data?: {
      forecasts?: ForecastResponse[];
    };
  };
  [key: string]: unknown;
}

export function DetailsModal({ sku, isOpen, onClose, onSkuChange, projectType }: DetailsModalProps) {
  const dispatch = useAppDispatch();
  const selectedSkus = useAppSelector(selectSelectedProducts);
  const [product, setProduct] = useState<ProductDetails | null>(null);
  const [similarProducts, setSimilarProducts] = useState<ProductDetails[]>([]);
  const [similarityDataMap, setSimilarityDataMap] = useState<Record<string, any>>({});
  // SKU che arrivano dalle sovrascritture del buyer invece che dal modello.
  const [overrideSkus, setOverrideSkus] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeCodModelloSelection, setIncludeCodModelloSelection] = useState(true);
  const [activeTab, setActiveTab] = useState<'dati' | 'simili' | 'venduti'>('dati');
  const [modalStack, setModalStack] = useState<ModalStackItem[]>([]);
  const [currentSku, setCurrentSku] = useState<string | null>(sku);
  const [geographicSalesData, setGeographicSalesData] = useState<any>(null);
  const [heatmapGridData, setHeatmapGridData] = useState<any>(null);
  const [forecastChartData, setForecastChartData] = useState<ForecastResponse[] | null>(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const [availableStores, setAvailableStores] = useState<string[]>([]);
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([]);
  const [selectedSeasons, setSelectedSeasons] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const isSelected = useMemo(() => (currentSku ? selectedSkus.includes(currentSku) : false), [selectedSkus, currentSku]);

  // Extract available sort keys from similarity data
  const availableSortKeys = useMemo(() => {
    const keys = new Set<string>();
    Object.values(similarityDataMap).forEach(similarityData => {
      if (similarityData) {
        Object.keys(similarityData).forEach(key => {
          if (typeof similarityData[key] === 'number' && similarityData[key] > 0) {
            keys.add(key);
          }
        });
      }
    });
    return Array.from(keys).sort();
  }, [similarityDataMap]);

  // Sort similar products based on selected criteria
  const sortedSimilarProducts = useMemo(() => {
    if (!sortBy || !similarProducts.length) {
      return similarProducts;
    }

    // Gli SKU imposti dal buyer restano in testa, nell'ordine che ha scelto lui:
    // sono una decisione, non un suggerimento da riordinare. L'ordinamento vale
    // solo sui risultati del modello.
    const imposti = similarProducts.filter((p) => overrideSkus.has(p.sku));
    const daOrdinare = similarProducts.filter((p) => !overrideSkus.has(p.sku));

    const sorted = daOrdinare.sort((a, b) => {
      const aValue = similarityDataMap[a.sku]?.[sortBy] ?? -1;
      const bValue = similarityDataMap[b.sku]?.[sortBy] ?? -1;

      if (sortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return [...imposti, ...sorted];
  }, [similarProducts, similarityDataMap, sortBy, sortDirection, overrideSkus]);

  // Function to minimize the current modal
  const minimizeCurrentModal = useCallback(() => {
    if (currentSku && product) {
      const stackItem: ModalStackItem = {
        sku: currentSku,
        product,
        similarProducts,
        similarityDataMap,
        activeTab,
        includeCodModelloSelection,
        selectedSeasons,
      };
      setModalStack(prev => {
        // Check if the product is already present in the stack
        const existingIndex = prev.findIndex(item => item.sku === currentSku);
        if (existingIndex !== -1) {
          // If it exists, update the existing state
          const newStack = [...prev];
          newStack[existingIndex] = stackItem;
          return newStack;
        }
        // Otherwise add the new item
        return [...prev, stackItem];
      });
      // Close the modal after minimizing
      onClose();
    }
  }, [currentSku, product, similarProducts, similarityDataMap, activeTab, includeCodModelloSelection, selectedSeasons, onClose]);

  // Function to restore a modal from the stack
  const restoreModal = useCallback((index: number) => {
    const item = modalStack[index];
    if (item) {
      // Restore the state from the minimized modal first
      setCurrentSku(item.sku);
      setProduct(item.product);
      setSimilarProducts(item.similarProducts);
      setSimilarityDataMap(item.similarityDataMap);
      setActiveTab(item.activeTab);
      setIncludeCodModelloSelection(item.includeCodModelloSelection);
      setSelectedSeasons(item.selectedSeasons);
      
      // If modal is currently open with different content, swap the current state with the minimized one
      if (currentSku && product && isOpen && currentSku !== item.sku) {
        const currentItem: ModalStackItem = {
          sku: currentSku,
          product,
          similarProducts,
          similarityDataMap,
          activeTab,
          includeCodModelloSelection,
          selectedSeasons,
        };
        setModalStack(prev => {
          const newStack = [...prev];
          newStack.splice(index, 1);
          newStack.push(currentItem);
          return newStack;
        });
      } else {
        // Remove the item from stack (will be removed when modal opens)
        setModalStack(prev => prev.filter((_, i) => i !== index));
      }
      
      // Notify parent to reopen the modal
      if (onSkuChange) {
        onSkuChange(item.sku);
      }
    }
  }, [modalStack, currentSku, product, similarProducts, similarityDataMap, activeTab, includeCodModelloSelection, selectedSeasons, onSkuChange, isOpen]);

  // Function to close a minimized modal
  const closeMinimizedModal = useCallback((index: number) => {
    setModalStack(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Function to open a new modal (called when clicking on DETTAGLI)
  const openNewModal = useCallback((newSku: string) => {
    // Save current modal to stack before opening new one
    if (currentSku && product) {
      const stackItem: ModalStackItem = {
        sku: currentSku,
        product,
        similarProducts,
        similarityDataMap,
        activeTab,
        includeCodModelloSelection,
        selectedSeasons,
      };
      setModalStack(prev => {
        // Check if the product is already present in the stack
        const existingIndex = prev.findIndex(item => item.sku === currentSku);
        if (existingIndex !== -1) {
          // If it exists, update the existing state
          const newStack = [...prev];
          newStack[existingIndex] = stackItem;
          return newStack;
        }
        // Otherwise add the new item
        return [...prev, stackItem];
      });
    }
    
    // Open new product in current modal
    setCurrentSku(newSku);
    setProduct(null);
    setSimilarProducts([]);
    setSimilarityDataMap({});
    setActiveTab('dati');
    setIncludeCodModelloSelection(true);
    setSelectedSeasons([]);
    setLoading(false);
    setError(null);
  }, [currentSku, product, similarProducts, similarityDataMap, activeTab, includeCodModelloSelection, selectedSeasons]);

  // Synchronize currentSku with sku prop when the modal opens
  useEffect(() => {
    if (isOpen && sku) {
      // Update currentSku only if it's different from the incoming sku
      if (currentSku !== sku) {
        setCurrentSku(sku);
      }
    } else if (!isOpen) {
      // Reset currentSku when modal closes
      setCurrentSku(null);
    }
  }, [isOpen, sku]);

  // Load available seasons when modal opens
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    
    async function loadSeasons() {
      try {
        const payload = await dispatch(
          executeSophiaAction({
            actionName: 'available-seasons',
            params: {},
            refreshThread: false,
          }),
        ).unwrap();
        const response = payload.data as any;
        const seasons = response?.['global-page-data']?.['seasons_list'] ?? [];
        setAvailableSeasons(seasons);
      } catch (err) {
        console.error('Error loading seasons:', err);
        setAvailableSeasons([]);
      }
    }
    
    void loadSeasons();
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!isOpen || !currentSku) {
      return;
    }
    async function loadDetails() {
      try {
        setLoading(true);
        const params: any = {
          'global-page-name': 'product-details',
          'global-page-name-sku': currentSku,
          'on_cod_modello': includeCodModelloSelection,
        };
        
        // Add seasons only if some are selected
        if (selectedSeasons.length > 0) {
          params['seasons'] = selectedSeasons;
        }
        
        const payload = await dispatch(
          executeSophiaAction({
            actionName: 'global-get-page-data',
            params,
            refreshThread: false,
          }),
        ).unwrap();
        const response = payload.data as SophiaDetailsResponse;
        const data = response?.['global-page-data'];
        
        const main = normaliseProductDetails(data?.sku_data);
        
        // Create a map to store similarity data by SKU and normalize product details
        const similarityMap: Record<string, any> = {};
        // SKU imposti dal buyer tramite la tabella delle sovrascritture: vanno distinti a
        // colpo d'occhio da quelli suggeriti dal modello.
        const overrideSkuSet = new Set<string>();
        const rawSimilarityData = data?.similarity_data;
        const similars = (Array.isArray(rawSimilarityData) ? rawSimilarityData : [])
          .map(item => {
            const normalizedProduct = normaliseProductDetails(item?.sku_data);

            // Store similarity data by SKU for later use
            if (normalizedProduct.sku && item?.similarity) {
              similarityMap[normalizedProduct.sku] = item.similarity;
            }
            if (normalizedProduct.sku && item?.source === 'override') {
              overrideSkuSet.add(normalizedProduct.sku);
            }

            return normalizedProduct;
          })
          .filter(productItem => productItem.sku);
        setOverrideSkus(overrideSkuSet);
        
        // Extract and set heatmap data
        const heatmapStats = data?.stats_heatmap?.stats;
        if (heatmapStats && Array.isArray(heatmapStats)) {
          setHeatmapGridData(heatmapStats);
        } else {
          setHeatmapGridData([]);
        }
        
        // Extract and set geographic sales data
        const worldStats = data?.stats_world?.stats;
        if (worldStats && Array.isArray(worldStats)) {
          const transformedData = {
            locations: worldStats
              .filter((stat: any) => stat.lat !== 0 && stat.lon !== 0 && stat.quantita_totale > 0)
              .map((stat: any) => ({
                // Main fields
                cod_negozio: stat.cod_negozio,
                city: stat.cod_negozio,
                country: stat.cod_negozio, // Could be enhanced with proper country mapping
                lat: stat.lat,
                lon: stat.lon,
                sales: stat.quantita_totale,
                
                // Additional fields for tooltip
                sku: stat.sku,
                quantita_totale: stat.quantita_totale,
                quantita_media: stat.quantita_media,
                quantita_mediana: stat.quantita_mediana,
                quantita_std: stat.quantita_std,
                date_min: stat.date_min,
                date_max: stat.date_max,
              }))
          };
          setGeographicSalesData(transformedData);
        } else {
          setGeographicSalesData({ locations: [] });
        }
        
        setProduct(main);
        setSimilarProducts(similars);
        setSimilarityDataMap(similarityMap);
        setError(null);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    }
    void loadDetails();
  }, [dispatch, includeCodModelloSelection, selectedSeasons, isOpen, currentSku]);

  // Load available stores when opening the venduti tab
  useEffect(() => {
    if (!isOpen || !currentSku || activeTab !== 'venduti') {
      return;
    }
    
    async function loadStores() {
      try {
        const payload = await dispatch(
          executeSophiaAction({
            actionName: "list-available-shops",
            params: {},
            refreshThread: false,
          })
        ).unwrap();
        const response = payload.data as { "global-page-data": { "shops_list": string[] } };
        const shops = response?.["global-page-data"]?.["shops_list"] ?? [];
        setAvailableStores(shops);
        
        // Set first store as default if not already selected
        if (!selectedStore && shops.length > 0) {
          setSelectedStore(shops[0]);
        }
      } catch (err) {
        console.error('Error loading stores:', err);
      }
    }
    
    void loadStores();
  }, [dispatch, isOpen, currentSku, activeTab]);

  // Load forecast data when store is selected
  useEffect(() => {
    if (!isOpen || !currentSku || activeTab !== 'venduti' || !selectedStore) {
      return;
    }
    
    async function loadForecast() {
      try {
        setForecastLoading(true);
        
        // Set the store first
        await dispatch(
          executeSophiaAction({
            actionName: "forecast-set-store",
            params: { "forecast-store": selectedStore },
            refreshThread: false,
          })
        ).unwrap();
        
        // Then load forecast data with the store already set
        const forecastPayload = await dispatch(
          executeSophiaAction({
            actionName: "global-get-page-data",
            params: { 
              "global-page-name": "forecast",
              ...(currentSku ? { "global-page-name-sku": currentSku } : {})
            },
            refreshThread: false,
          })
        ).unwrap();
        
        const response = forecastPayload.data as any;
        const forecastData = response?.["global-page-data"]?.forecast_data;
        
        if (forecastData?.forecasts && Array.isArray(forecastData.forecasts)) {
          setForecastChartData(forecastData.forecasts);
        } else {
          setForecastChartData(null);
        }
      } catch (err) {
        console.error('Error loading forecast:', err);
        setForecastChartData(null);
      } finally {
        setForecastLoading(false);
      }
    }
    
    void loadForecast();
  }, [dispatch, isOpen, currentSku, activeTab, selectedStore]);

  const handleToggleSelection = async (targetSku: string, selected: boolean) => {
    await dispatch(
      executeSophiaAction({
        actionName: selected ? 'global-unset-select-product' : 'global-set-select-product',
        params: { 'global-select-product': targetSku },
        refreshThread: true,
      }),
    );
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: detailsStyles }} />
      <style dangerouslySetInnerHTML={{ __html: GalleryStyles }} />
      <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex max-w-[95vw] flex-col items-center overflow-hidden bg-white p-2" style={{ width: '960px' }}>
        <div className="sticky top-0 flex w-full items-center justify-between gap-2 bg-white p-2">
          <div className="flex flex-row items-center justify-start">
            <h2 className="pl-3 text-2xl font-bold">Dettagli Prodotto</h2>
          </div>
          <div className="flex flex-row justify-end gap-2">
            {currentSku && projectType !== 'optimization' && (
              <button
                type="button"
                onClick={() => handleToggleSelection(currentSku, isSelected)}
                className={`flex items-center justify-center gap-x-2 px-2 py-2 text-sm font-semibold ${
                  isSelected 
                    ? 'bg-black text-white border-2 border-black' 
                    : 'bg-white text-black border-2 border-black'
                }`}
              >
                {isSelected ? 'SELEZIONATO' : 'SELEZIONA'}
                <span
                  className={`h-4 w-4 flex items-center justify-center ${
                    isSelected 
                      ? 'bg-black border-2 border-white' 
                      : 'bg-white border-2 border-black'
                  }`}
                >
                  {isSelected && <span className="text-white text-xs">✓</span>}
                </span>
              </button>
            )}
            <button 
              type="button" 
              onClick={minimizeCurrentModal} 
              className="flex items-center justify-center w-10 h-10 text-2xl hover:bg-gray-100 transition-colors"
              title="Minimizza"
            >
              −
            </button>
            <button type="button" onClick={onClose} className="flex items-center justify-center w-10 h-10 text-2xl hover:bg-gray-100 transition-colors">×</button>
          </div>
        </div>
        <div className="h-px w-full bg-gray-200" />
        {loading && <p className="p-4 text-sm text-gray-500">Loading…</p>}
        {error && <p className="p-4 text-sm text-red-500">{error}</p>}
        {!loading && !error && product && (
          <div className="flex w-full max-w-[95vw] flex-col items-center gap-4 rounded-2xl bg-white p-2">
            <h3 className="text-3xl font-semibold text-center">{product.code}</h3>
            <ImageCarousel images={product.images} productCode={product.code} />
            
            {/* Tab Navigation */}
            <div className="flex w-full">
              <button
                type="button"
                onClick={() => setActiveTab('dati')}
                className={`flex-1 px-4 py-3 text-lg font-semibold transition-colors ${
                  activeTab === 'dati'
                    ? 'bg-black text-white border-b-4 border-black'
                    : 'bg-white text-black border-2 border-black'
                }`}
              >
                DATI
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('simili')}
                className={`flex-1 px-4 py-3 text-lg font-semibold transition-colors ${
                  activeTab === 'simili'
                    ? 'bg-black text-white border-b-4 border-black'
                    : 'bg-white text-black border-2 border-black'
                }`}
              >
                PRODOTTI SIMILI
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('venduti')}
                className={`flex-1 px-4 py-3 text-lg font-semibold transition-colors ${
                  activeTab === 'venduti'
                    ? 'bg-black text-white border-b-4 border-black'
                    : 'bg-white text-black border-2 border-black'
                }`}
              >
                FORECAST
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'dati' && (
              <div className="w-full border border-black">
                <div className="flex w-full flex-col gap-4 p-4">
                {/* First row: Three boxes side by side */}
                {/* Colors */}
                <div className="bg-gray-100 px-4 py-3">
                <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Varianti Colore</span>
                {(() => {
                    const colorComposition = product.details['color_composition'];
                    return colorComposition && typeof colorComposition === 'string' ? (
                    <ColorCompositionDisplay 
                      colorComposition={colorComposition}
                      currentSku={currentSku || undefined}
                      onVariantClick={openNewModal}
                    />
                    ) : null;
                })()}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  {/* Price */}
                  <div className="bg-gray-100 px-4 py-3">
                    <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">Listino IT</span>
                    {(() => {
                      const prezzo = product.details['prezzo'];
                      return prezzo ? (
                        <span className="mt-1 block text-2xl font-bold text-gray-900">
                          {String(prezzo) + '€'}
                        </span>
                      ) : null;
                    })()}
                  </div>

                  {/* Yarn Type */}
                  <div className="bg-gray-100 px-4 py-3">
                    <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">Tipo Filato</span>
                    {(() => {
                      const tipoFilato = product.details['tipo_filato'];
                      return tipoFilato ? (
                        <span className="mt-1 block text-2xl font-bold text-gray-900">
                          {String(tipoFilato)}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Second row: Description and Model Attributes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Description */}
                  <div className="bg-gray-100 px-4 py-3">
                    <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">Descrizione SFCC</span>
                    {(() => {
                      const description = product.details['descrizione'];
                      return description ? (
                        <ExpandableText text={String(description)} />
                      ) : null;
                    })()}
                  </div>

                  {/* Model Attributes */}
                  <div className="bg-gray-100 px-4 py-3">
                    <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">Attributi Modellario</span>
                    {(() => {
                      const attributes = product.details['attr_modellario'];
                      return attributes ? (
                        <span className="mt-1 block text-lg font-semibold text-gray-900">
                          {String(attributes)}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Dynamic fields - show only fields that exist */}
                {(() => {
                  // List of fields to exclude (already shown above or special cases)
                  const excludedFields = new Set([
                    'color_composition', 'embedding color',
                    'prezzo',
                    'tipo_filato',
                    'descrizione', 'description',
                    'attr_modellario',
                    'sketch_url', 'url_img_list' // Image fields
                  ]);

                  // Field display configuration - fields that should be expandable
                  const expandableFields = new Set([
                    'long_descr',
                    'bgadescriz1',
                    'costruzione_vestibilita_uso',
                    'des_modellario',
                    'pattern_dettagli',
                    'composizione',
                    'des_modello',
                    'desc_model_group',
                  ]);

                  // Build fieldConfig dynamically from MAPPING_COLUMNS
                  const fieldConfig: Record<string, { label: string, expandable?: boolean }> = {};
                  Object.entries(MAPPING_COLUMNS).forEach(([key, label]) => {
                    fieldConfig[key] = {
                      label: label.charAt(0).toUpperCase() + label.slice(1), // Capitalize first letter
                      expandable: expandableFields.has(key)
                    };
                  });

                  // Collect available fields
                  const availableFields: Array<{ key: string, value: unknown, config: { label: string, expandable?: boolean } }> = [];
                  
                  Object.entries(product.details).forEach(([key, value]) => {
                    if (!excludedFields.has(key) && value !== null && value !== undefined && value !== '' && value !== 'N/A') {
                      const config = fieldConfig[key];
                      if (config) {
                        availableFields.push({ key, value, config });
                      }
                    }
                  });

                  // Sort fields alphabetically by label
                  availableFields.sort((a, b) => a.config.label.localeCompare(b.config.label));

                  // Group fields into rows of 3
                  const rows: Array<Array<{ key: string, value: unknown, config: { label: string, expandable?: boolean } }>> = [];
                  for (let i = 0; i < availableFields.length; i += 3) {
                    rows.push(availableFields.slice(i, i + 3));
                  }

                  return rows.map((row, rowIndex) => (
                    <div key={rowIndex} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {row.map((field) => (
                        <div key={field.key} className="bg-gray-100 px-4 py-3">
                          <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                            {field.config.label}
                          </span>
                          {field.config.expandable ? (
                            <ExpandableText text={formatColumnValue(field.key, field.value)} />
                          ) : (
                            <span className="mt-1 block text-lg font-semibold text-gray-900">
                              {formatColumnValue(field.key, field.value)}
                            </span>
                          )}
                        </div>
                      ))}
                      {/* Fill empty slots if row has less than 3 items */}
                      {row.length < 3 && Array.from({ length: 3 - row.length }).map((_, i) => (
                        <div key={`empty-${i}`} className="rounded-2xl bg-gray-100 px-4 py-3" />
                      ))}
                    </div>
                  ));
                })()}
                </div>
              </div>
            )}

            {activeTab === 'venduti' && (
              <div className="w-full">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="flex flex-col items-center gap-4">
                      <div className="animate-spin-in rounded-full h-16 w-16 border-b-4 border-black"></div>
                      <p className="text-gray-600 font-medium">Caricamento dati...</p>
                    </div>
                  </div>
                ) : (
                  <div className="w-full border border-black">
                    <div className="flex w-full flex-col gap-6 p-4">
                      {/* Heatmap Grid Section */}
                      <div className="w-full">
                        <h3 className="text-xl font-semibold mb-4">Distribuzione per Area Geografica</h3>
                        <p className="text-sm text-gray-600 mb-2">
                          Classificazione delle vendite/forecast per area geografica
                        </p>
                        {heatmapGridData && Array.isArray(heatmapGridData) && heatmapGridData.length > 0 ? (
                          <div className="w-full bg-white border border-gray-200 shadow-sm">
                            <HeatmapGrid data={heatmapGridData} />
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-32 bg-gray-50 border border-gray-200">
                            <p className="text-gray-500">
                              {heatmapGridData === null ? 'Caricamento dati heatmap...' : 'Nessun dato disponibile per la heatmap'}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Chart Section */}
                      <div className="w-full">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-xl font-semibold mb-2">Distribuzione Vendite</h3>
                            <p className="text-sm text-gray-600">
                              Visualizzazione della distribuzione geografica delle vendite previste per negozio
                            </p>
                          </div>
                        </div>
                        {geographicSalesData && geographicSalesData.locations && geographicSalesData.locations.length > 0 ? (
                          <div className="w-full bg-white border border-gray-200 overflow-hidden shadow-sm">
                            <GeographicSalesMap data={geographicSalesData} />
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-64 bg-gray-50 border border-gray-200">
                            <p className="text-gray-500">
                              {geographicSalesData === null ? 'Caricamento dati distribuzione vendite geografica...' : 'Nessun dato disponibile per la mappa geografica'}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Forecast Chart Section */}
                      <div className="w-full">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-xl font-semibold mb-2">Previsione Vendite (Forecast)</h3>
                            <p className="text-sm text-gray-600">
                              Andamento previsto delle vendite nel tempo per questo prodotto
                            </p>
                          </div>
                          
                          {/* Store Selection Dropdown */}
                          {availableStores.length > 0 && (
                            <div className="flex items-center gap-2">
                              <label htmlFor="store-select" className="text-sm text-gray-600">
                                Negozio:
                              </label>
                              <select
                                id="store-select"
                                value={selectedStore ?? ''}
                                onChange={(e) => setSelectedStore(e.target.value)}
                                className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white"
                                disabled={forecastLoading}
                              >
                                {availableStores.map((store) => (
                                  <option key={store.split(' - ')[0]} value={store.split(' - ')[0]}>
                                    {store}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                        
                        {forecastLoading ? (
                          <div className="flex flex-col items-center justify-center h-64 bg-gray-50 border border-gray-200">
                            <div className="animate-spin-in rounded-full h-10 w-10 border-b-2 border-blue-500 mb-3"></div>
                            <p className="text-gray-500 text-sm">Caricamento forecast per {selectedStore}...</p>
                          </div>
                        ) : forecastChartData && forecastChartData.length > 0 ? (
                          <div className="w-full bg-white border border-gray-200 overflow-hidden shadow-sm p-4">
                            <ForecastChart 
                              data={forecastChartData} 
                              height={300}
                              showDisaggregated={false}
                            />
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-64 bg-gray-50 border border-gray-200">
                            <p className="text-gray-500">
                              {selectedStore ? `Nessun dato di forecast disponibile per ${selectedStore}` : 'Seleziona un negozio per visualizzare il forecast'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'simili' && (
              <div className="w-full">
                <div className="flex w-full items-center justify-end gap-3 mb-3">
                  {/* Season Selector */}
                  <div className="flex items-center gap-2">
                    <label htmlFor="season-select" className="text-sm font-medium text-gray-700">
                      Stagione:
                    </label>
                    <select
                      id="season-select"
                      value={selectedSeasons.length > 0 ? selectedSeasons[0] : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setSelectedSeasons([]);
                        } else {
                          setSelectedSeasons([value]);
                        }
                      }}
                      className="text-sm border-2 border-black px-3 py-2.5 bg-white min-w-[150px]"
                    >
                      <option value="">Tutte</option>
                      {availableSeasons.map((season) => (
                        <option key={season} value={season}>
                          {season}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sort By Selector */}
                  <div className="flex items-center gap-2">
                    <label htmlFor="sort-select" className="text-sm font-medium text-gray-700">
                      Ordina per:
                    </label>
                    <select
                      id="sort-select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="text-sm border-2 border-black px-3 py-2.5 bg-white min-w-[150px]"
                    >
                      <option value="">Nessun ordinamento</option>
                      {availableSortKeys.map((key) => (
                        <option key={key} value={key}>
                          {key}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sort Direction Toggle */}
                  {sortBy && (
                    <button
                      type="button"
                      onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                      className="flex items-center justify-center gap-x-2 px-3 py-2 text-sm font-semibold bg-white text-black border-2 border-black"
                      title={sortDirection === 'asc' ? 'Ascendente' : 'Discendente'}
                    >
                      {sortDirection === 'asc' ? '↑ Crescente' : '↓ Decrescente'}
                    </button>
                  )}
                  
                  {/* Include Same Model Button */}
                  <button
                    type="button"
                    onClick={() => setIncludeCodModelloSelection((prev) => !prev)}
                    className={`flex items-center justify-center gap-x-2 px-2 py-2 text-sm font-semibold ${
                      !includeCodModelloSelection
                        ? 'bg-black text-white border-2 border-black'
                        : 'bg-white text-black border-2 border-black'
                    }`}
                  >
                    INCLUDI STESSO MODELLO
                    <span
                      className={`h-4 w-4 flex items-center justify-center ${
                        !includeCodModelloSelection
                          ? 'bg-black border-2 border-white'
                          : 'bg-white border-2 border-black'
                      }`}
                    >
                      {!includeCodModelloSelection && <span className="text-white text-xs">✓</span>}
                    </span>
                  </button>
                </div>
                <div className="w-full border border-black">
                <div className="flex w-full flex-col gap-3 p-2">
                {sortedSimilarProducts.map((item, index) => {
                  const similarSelected = selectedSkus.includes(item.sku);
                  const coordinateData = parseCoordinateData(item, similarityDataMap[item.sku]);
                  const isEven = index % 2 === 0;
                  return (
                    <div
                      key={item.sku ?? index}
                      className={`flex w-full flex-nowrap items-center gap-3 p-3 ${isEven ? 'bg-white' : ''}`}
                      style={!isEven ? { backgroundColor: '#F3F3F3' } : {}}
                    >
                      <span className="w-6 text-sm font-semibold text-gray-700">{index + 1}</span>
                      <SimpleImageCarousel
                        images={item.images}
                        productCode={item.code}
                        width={80}
                        height={80}
                        className="flex-none"
                      />
                      <span className="flex-1 truncate text-base">
                        {item.code}
                        {overrideSkus.has(item.sku) && (
                          <span
                            className="ml-2 whitespace-nowrap border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-tight align-middle"
                            style={{ backgroundColor: '#B99EA6', borderColor: '#A5838C', color: '#1f2937' }}
                            title="Prodotto indicato dal buyer, non suggerito dal modello"
                          >
                            Buyer
                          </span>
                        )}
                      </span>
                      <div className="h-28 w-28 flex-none mr-4">
                        {coordinateData.length > 0 ? (
                          <PieChart 
                            data={coordinateData} 
                            width={112} 
                            height={112}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-full">
                            <span className="text-xs text-gray-500 text-center">Nessun dato</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-start gap-2">
                        {projectType !== 'optimization' && (
                          <button
                            type="button"
                            onClick={() => handleToggleSelection(item.sku, similarSelected)}
                            className={`flex items-center justify-center gap-x-2 px-2 py-2 text-sm font-semibold w-40 ${
                              similarSelected
                                ? 'bg-black text-white border-2 border-black'
                                : 'bg-white text-black border-2 border-black'
                            }`}
                          >
                            {similarSelected ? 'SELEZIONATO' : 'SELEZIONA'}
                            <span
                              className={`h-4 w-4 flex items-center justify-center ${
                                similarSelected 
                                  ? 'bg-black border-2 border-white' 
                                  : 'bg-white border-2 border-black'
                              }`}
                            >
                              {similarSelected && <span className="text-white text-xs">✓</span>}
                            </span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openNewModal(item.sku)}
                          className="px-3 py-2 text-sm font-semibold bg-white text-black border-2 border-black w-40"
                        >
                          DETTAGLI
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </Modal>
      
      {/* Barra dei modal minimizzati */}
      {modalStack.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-2 bg-black p-2 shadow-lg">
          <span className="text-white text-sm font-semibold px-2 py-1">Prodotti Minimizzati:</span>
          {modalStack.map((item, index) => (
            <div
              key={`${item.sku}-${index}`}
              className="flex items-center gap-2 bg-white px-3 py-1 border-2 border-white"
            >
              <span className="text-sm font-semibold text-gray-900">{item.product?.code || item.sku}</span>
              <button
                type="button"
                onClick={() => restoreModal(index)}
                className="text-black hover:text-gray-600 px-1"
                title="Ripristina"
              >
                <i className="ti-angle-up text-xl" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => closeMinimizedModal(index)}
                className="text-black hover:text-gray-600 leading-none px-1"
                title="Chiudi"
              >
                <i className="ti-close text-xl" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default DetailsModal;
